import { Link, type ErrorComponentProps } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export default function ErrorFallback({ error, reset }: ErrorComponentProps) {
  // Raw exception messages can contain user input (project context,
  // tokens, PII passed through to an API client). Render them only in DEV.
  // In production the user sees the friendly copy; the real error is reported
  // to Sentry by the route boundary above.
  const message = error?.message || String(error)
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex size-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="size-5" aria-hidden />
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
          <CardDescription>
            An unexpected error interrupted this page. You can try again or go
            back to the start.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {import.meta.env.DEV ? (
            <pre className="max-h-40 overflow-auto rounded-md bg-muted p-3 text-left text-xs whitespace-pre-wrap break-words">
              {message}
            </pre>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="flex-1" onClick={() => reset()}>
              Try again
            </Button>
            <Button variant="outline" className="flex-1" asChild>
              <Link to="/dashboard">Go home</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
